import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { messaggioErrore } from '../utils/apiError';
import * as sessionsApi from '../api/sessions';

export const fetchSessions = createAsyncThunk('sessions/fetch', async () => {
  const { data } = await sessionsApi.listSessions();
  return data;
});

export const createSessionAsync = createAsyncThunk('sessions/create', async (_, { rejectWithValue }) => {
  try {
    const { data } = await sessionsApi.createSession();
    return data;
  } catch (err) {
    // Il motivo vero (per esempio il limite di connessioni) va mostrato:
    // «errore nella creazione» non dice cosa fare.
    return rejectWithValue(err.response?.data?.error || messaggioErrore(err, 'Collegamento non riuscito.'));
  }
});

export const deleteSessionAsync = createAsyncThunk('sessions/delete', async (id) => {
  await sessionsApi.deleteSession(id);
  return id;
});

export const relinkSessionAsync = createAsyncThunk('sessions/relink', async (id) => {
  const { data } = await sessionsApi.relinkSession(id);
  return data;
});

export const restartSessionAsync = createAsyncThunk('sessions/restart', async (id) => {
  const { data } = await sessionsApi.restartSession(id);
  return data;
});

// Stati in cui un QR già ricevuto vale ancora.
const ATTESA_QR = ['initializing', 'qr_pending'];

const sessionSlice = createSlice({
  name: 'sessions',
  initialState: { items: [], loading: false, error: null, sync: {} },
  reducers: {
    updateSessionStatus(state, { payload }) {
      const session = state.items.find((s) => s.session_id === payload.userId);
      if (session && session.status !== payload.status) {
        session.status = payload.status;
        // Il conto di "da quanto e' ferma" riparte qui: altrimenti un cambio
        // di stato arrivato dal socket userebbe l'istante del sondaggio
        // precedente e la sessione sembrerebbe bloccata da prima.
        session.status_changed_at = new Date().toISOString();
      }
    },
    // Avanzamento della sincronizzazione annunciato dal gateway, per connessione.
    setSyncProgress(state, { payload }) {
      if (!state.sync) state.sync = {};
      state.sync[payload.userId] = payload;
    },
    setSessionQR(state, { payload }) {
      const session = state.items.find((s) => s.session_id === payload.userId);
      if (session) session.qr_code = payload.qrCode;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessions.pending, (state) => { state.loading = true; })
      .addCase(fetchSessions.fulfilled, (state, { payload }) => {
        state.loading = false;
        // Il QR non sta nel database: arriva dal socket o da /qr/. Senza
        // questo, il sondaggio ogni 5 secondi lo cancellava e il QR spariva
        // dopo un attimo (29/09, primo Ricollega su wozzap.it).
        const prima = new Map(state.items.map(s => [s.id, s.qr_code]));
        state.items = payload.map(s => (!s.qr_code && ATTESA_QR.includes(s.status) && prima.get(s.id)
          ? { ...s, qr_code: prima.get(s.id) } : s));
      })
      .addCase(fetchSessions.rejected, (state, { error }) => {
        state.loading = false; state.error = error.message;
      })
      .addCase(createSessionAsync.fulfilled, (state, { payload }) => {
        state.items.push(payload);
      })
      .addCase(deleteSessionAsync.fulfilled, (state, { payload }) => {
        state.items = state.items.filter((s) => s.id !== payload);
      })
      .addCase(relinkSessionAsync.fulfilled, (state, { payload }) => {
        const i = state.items.findIndex(s => s.id === payload.id);
        if (i >= 0) state.items[i] = payload;
      })
      .addCase(restartSessionAsync.fulfilled, (state, { payload }) => {
        state.items = state.items.map((s) => (s.id === payload.id ? payload : s));
      });
  },
});

export const { updateSessionStatus, setSessionQR, setSyncProgress } = sessionSlice.actions;
export default sessionSlice.reducer;
